/**
 * Desktop view of the cut-to-size modal (from the `lg` breakpoint up): a modal dialog with the
 * product image and stock attributes under "Cut From", then the quantity and one input or dropdown
 * per cut field under "Cut To", and Apply.
 *
 * Rendering only. All state, options and validation come from `DefineCutModal.tsx` through
 * `DefineCutProps`; `DefineCutModalMobile.tsx` renders the same props for smaller screens, and a
 * field change usually belongs in both. Fields are matched by `content.cutAttributes.*.value`
 * (see `DefineCutModalContent.ts`). Dimension labels carry the inch unit; inputs accept up to
 * four decimals.
 */
import { useTheme } from "@ryerson/frontend.theme";
import React from "react";
import { DefineCutProps } from "./DefineCutModal";
import { Button } from "@ryerson/frontend.button";
import { Typography } from "@ryerson/frontend.typography";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Divider } from "@components/CustomerDashboard/Helpers/StyledComponents";
import { ModalContainer } from "@components/ShoppingListDashboard/Helpers/StyledComponents";
import { Dropdown, InputType, NumberInput } from "@ryerson/frontend.form-controls";
import {
	CutAttribute,
	GenericDropDownType,
	displayProductShapeForm,
	getShortDescriptionFromRowData,
	getCompanyLink,
} from "./DefineCutHelper";
import { inch } from "@ryerson/common.uom";
import styled from "@emotion/styled";
import { Tooltip } from "@ryerson/frontend.modal";
import AttributeIcon from "@components/Shared/AttributeIcon/AttributeIcon";
import { Alert } from "@ryerson/frontend.notification";
import { Link } from "@ryerson/frontend.navigation";
import { UpdatedProductAttribute } from "@components/Shared/ProductDisplay/Helpers/ProductDisplay.models";
import { NetworkOperationResult } from "@components/constants";
import { getAttributeNameFromArray } from "@utils/translateAttributes";
import { LanguageToLocale } from "@ryerson/frontend.common";

const TooltipContent = styled(Typography)`
	margin: 10px 0px;
`;

/** Desktop cut-to-size modal; see the file header. */
const DefineCutModalDesktop: React.FC<DefineCutProps> = ({
	showDefineCutModal,
	handleClose,
	content,
	product,
	customSubtitle,
	cutAttributes,
	language,
	defineCutParameters,
	updateInputValue,
	updateDropdownValue,
	cutOptions,
	finishOptions,
	processOptions,
	toleranceOptions,
	handleApply,
	formSubmitted,
	validateCutParameters,
	imageSource,
	helpText,
	productDetailsAPIFetchStatus,
	productCanBeCut,
}) => {
	const { theme } = useTheme();
	const { lighterGray, background, link, header } = theme.colors.primary;

	const locale = LanguageToLocale[language];

	const renderTooltip = (tooltipContent: string) => {
		return (
			<FlexItem>
				<Tooltip type="secondary" icon="help" size="sm">
					<TooltipContent size="xs" type="primary">
						{tooltipContent}
					</TooltipContent>
				</Tooltip>
			</FlexItem>
		);
	};

	const renderCutAttributeDropdown = (
		attribute: string,
		label: string,
		value: string | number,
		options: GenericDropDownType[],
		required: boolean
	): JSX.Element => {
		return (
			<Dropdown
				label={label}
				size="md"
				value={value}
				onChange={(value: string) => updateDropdownValue(value, attribute)}
				options={options}
				language={language}
				required={required}
				labelAddition={
					attribute === "processing" && helpText?.process && helpText?.process !== "" ? (
						renderTooltip(helpText?.process)
					) : attribute === "finish" && helpText?.finish && helpText?.finish !== "" ? (
						renderTooltip(helpText?.finish)
					) : attribute === "cutShape" &&
					  helpText?.cutShape &&
					  helpText?.cutShape !== "" ? (
						renderTooltip(helpText?.cutShape)
					) : (
						<></>
					)
				}
				dataTestId="define-cut-modal-dropdown"
			/>
		);
	};

	const renderCutAttributeInputField = (
		attribute: string,
		label: string,
		value: string | number,
		inputType: InputType,
		isRequired: boolean,
		cutAttribute: CutAttribute
	): JSX.Element => {
		return (
			<>
				<NumberInput
					label={label}
					name={attribute}
					placeholder={""}
					required={isRequired}
					value={value || ""}
					onChange={(event) => updateInputValue(event)}
					language={language}
					inputType={inputType}
					submitted={formSubmitted}
					isValid={cutAttribute.isValid}
					disabled={cutAttribute.disabled}
					validation={() => {
						validateCutParameters();
						return cutAttribute.error;
					}}
					precision={4}
					labelAddition={<AttributeIcon attribute={attribute} />}
					dataTestId="define-cut-modal-number-input"
				/>
			</>
		);
	};

	return (
		<ContentSection vPadding="0" hPadding="0">
			<ModalContainer
				title={content.cutToSize}
				closable={true}
				isOpen={showDefineCutModal}
				onClose={handleClose}
				type="primary"
				bodyType="secondary"
				modalStyle="default"
				buttonArea={
					<Flex
						direction="column"
						justifyContent="space-between"
						alignItems="flex-start"
						css={{ width: "100%" }}
					>
						<Divider theme={theme} />
						<Button
							size="lg"
							label={content.apply}
							onClick={handleApply}
							fullwidth={true}
							css={{ width: 244, fontWeight: "bold", paddingTop: "20px" }}
							disabled={cutAttributes.length === 0}
							dataTestId="define-cut-modal-apply-button"
						/>
					</Flex>
				}
			>
				<Flex
					direction="column"
					css={{ minHeight: cutAttributes.length === 0 ? 250 : 476 }}
				>
					<Flex
						direction="row"
						css={{
							background: lighterGray,
							gap: 100,
							padding: "29px 0px",
							minHeight: 264,
						}}
					>
						<img
							css={{ background: background, width: "200px", padding: 10 }}
							src={imageSource}
							alt=""
						/>

						<FlexItem css={{ minWidth: 550 }}>
							<Typography size="md" weight="bold" color={header}>
								{content.cutFrom}
							</Typography>
							<Flex
								justifyContent="space-between"
								direction="row"
								css={{ margin: "10px 0px", gap: 50 }}
							>
								<Typography size="md" weight="bold">
									{customSubtitle}
								</Typography>
								<Typography size="md">
									{getShortDescriptionFromRowData(product, language)}
								</Typography>
							</Flex>
							<Divider theme={theme} />
							<Flex direction="column" css={{ marginTop: 35 }}>
								<Typography
									size="md"
									weight="bold"
									variant="div"
									css={{ marginBottom: 10 }}
								>
									{displayProductShapeForm(product, language)}
								</Typography>
								{/* Two attributes per row: an even index renders itself and the
								next one; an odd index renders an empty item. */}
								{product?.attributes?.map(
									(attribute, index, attributesCollection) => {
										return index % 2 === 1 ? (
											<FlexItem key={index}></FlexItem>
										) : (
											<Flex
												direction="row"
												key={index}
												css={{ marginBottom: 10 }}
											>
												<Flex css={{ width: "50%" }}>
													<Flex
														css={{ width: "50%" }}
														alignItems="center"
													>
														<Typography
															size="md"
															weight="bold"
															css={{ marginRight: 5 }}
														>
															{getAttributeNameFromArray(
																attributesCollection,
																index,
																locale
															)}
														</Typography>
														{
															<AttributeIcon
																attribute={getAttributeNameFromArray(
																	attributesCollection,
																	index,
																	locale
																)}
															/>
														}
													</Flex>
													<Flex
														css={{ width: "50%" }}
														alignItems="center"
													>
														<Typography size="md">
															{
																(
																	attributesCollection[
																		index
																	] as UpdatedProductAttribute
																)?.localization?.[locale]?.value
															}
														</Typography>
													</Flex>
												</Flex>
												<Flex css={{ width: "50%", paddingLeft: "25px" }}>
													<Flex
														css={{ width: "50%" }}
														alignItems="center"
													>
														<Typography
															size="md"
															weight="bold"
															css={{ marginRight: 5 }}
														>
															{getAttributeNameFromArray(
																attributesCollection,
																index + 1,
																locale
															)}
														</Typography>
														{
															<AttributeIcon
																attribute={getAttributeNameFromArray(
																	attributesCollection,
																	index + 1,
																	locale
																)}
															/>
														}
													</Flex>
													<Flex
														css={{ width: "50%" }}
														alignItems="center"
													>
														<Typography size="md">
															{
																(
																	attributesCollection[
																		index + 1
																	] as UpdatedProductAttribute
																)?.localization?.[locale]?.value
															}
														</Typography>
													</Flex>
												</Flex>
											</Flex>
										);
									}
								)}
							</Flex>
						</FlexItem>
					</Flex>
					<ContentSection vPadding="0" hPadding="0">
						{/* If request is not in flight and product cannot be cut, display error message */}
						{productDetailsAPIFetchStatus !== NetworkOperationResult.PENDING &&
							!productCanBeCut && (
								<Flex
									alignItems="center"
									wrap="wrap"
									css={{
										background: background,
										padding: "0px 100px 0px 100px",
										margin: "0 -40px",
										gap: "20px 20px",
										width: 930,
										minHeight: "112px",
									}}
								>
									<Alert severity="error" size="md">
										<Typography>
											{content.errors.processMessage1}
											<Link
												next={false}
												target="_blank"
												to={`${getCompanyLink()}`}
												color={link}
												size="md"
											>
												{content.errors.companyLink.replace(
													"%url%",
													getCompanyLink()
												)}
											</Link>
											{content.errors.processMessage2}
											<Link
												next={false}
												to={`mailTo:${content.errors.brandedEmail}`}
												color={link}
												size="md"
											>
												{content.errors.brandedEmail}
											</Link>
											{content.errors.processMessage3}
										</Typography>
									</Alert>
								</Flex>
							)}

						{/* Only display cut input boxes if request is not in flight and this product has cut attributes */}
						{productDetailsAPIFetchStatus !== NetworkOperationResult.PENDING &&
							cutAttributes?.length > 0 && (
								<>
									<Flex
										css={{
											background: background,
											padding: "20px 40px 0",
											margin: "0 -40px",
											gap: "15px",
										}}
										direction="column"
									>
										<Typography size="md" weight="bold" color={header}>
											{content.cutTo}
										</Typography>
										<NumberInput
											label={content.quantityPcs}
											name="quantity"
											placeholder={""}
											required={true}
											value={defineCutParameters.quantity}
											css={{ width: "29%" }}
											onChange={(event) => updateInputValue(event)}
											language={language}
											labelAddition={
												<Flex css={{ marginLeft: "10px" }}>
													{renderTooltip(content.quantityTooltipMessage)}
												</Flex>
											}
											submitted={formSubmitted}
											isValid={
												formSubmitted
													? Boolean(defineCutParameters.quantity)
													: true
											}
											precision={4}
											dataTestId="define-cut-modal-quantity-number-input"
										/>
									</Flex>
									<Flex
										alignItems="flex-start"
										wrap="wrap"
										css={{
											background: background,
											padding: "20px 40px 20px 40px",
											margin: "0 -40px",
											gap: "30px 50px",
											width: 930,
											minHeight: "212px",
										}}
									>
										{cutAttributes.map(
											(cutAttribute: CutAttribute, index: number) => {
												return (
													<FlexItem css={{ width: "29%" }} key={index}>
														{cutAttribute.name ===
															content.cutAttributes.shape.value &&
															renderCutAttributeDropdown(
																"cutShape",
																content.cutAttributes.shape.display,
																defineCutParameters?.cutShape
																	?.value,
																cutOptions.filter(
																	(option) => !option.disabled
																),
																true
															)}
														{cutAttribute.name ===
															content.cutAttributes.processing
																.value &&
															renderCutAttributeDropdown(
																"processing",
																content.cutAttributes.processing
																	.display,
																defineCutParameters?.processing
																	?.value,
																processOptions.filter(
																	(option) => !option.disabled
																),
																true
															)}
														{cutAttribute.name ===
															content.cutAttributes.finish.value &&
															renderCutAttributeDropdown(
																"finish",
																content.cutAttributes.finish
																	.display,
																defineCutParameters?.finish?.value,
																finishOptions,
																false
															)}
														{cutAttribute.name ===
															content.cutAttributes.width.value &&
															renderCutAttributeInputField(
																"width",
																`${content.cutAttributes.width.display}, ${inch.display}`,
																defineCutParameters?.width,
																"number",
																true,
																cutAttribute
															)}
														{cutAttribute.name ===
															content.cutAttributes.length.value &&
															renderCutAttributeInputField(
																"length",
																`${content.cutAttributes.length.display}, ${inch.display}`,
																defineCutParameters?.length,
																"number",
																true,
																cutAttribute
															)}
														{cutAttribute.name ===
															content.cutAttributes.oneSide.value &&
															renderCutAttributeInputField(
																"oneSide",
																`${content.cutAttributes.oneSide.display}, ${inch.display}`,
																defineCutParameters?.oneSide,
																"number",
																true,
																cutAttribute
															)}
														{cutAttribute.name ===
															content.cutAttributes.oneSideTolerance
																.value &&
															renderCutAttributeDropdown(
																"oneSideTolerance",
																content.cutAttributes
																	.oneSideTolerance.display,
																defineCutParameters
																	?.oneSideTolerance?.value,
																toleranceOptions,
																true
															)}
														{cutAttribute.name ===
															content.cutAttributes.tolerance.value &&
															renderCutAttributeDropdown(
																"tolerance",
																content.cutAttributes.tolerance
																	.display,
																defineCutParameters?.tolerance
																	?.value,
																toleranceOptions,
																true
															)}
														{cutAttribute.name ===
															content.cutAttributes.outsideDiameter
																.value &&
															renderCutAttributeInputField(
																"outsideDiameter",
																`${content.cutAttributes.outsideDiameter.display}, ${inch.display}`,
																defineCutParameters?.outsideDiameter,
																"number",
																true,
																cutAttribute
															)}
														{cutAttribute.name ===
															content.cutAttributes.outsideTolerance
																.value &&
															renderCutAttributeDropdown(
																"outsideTolerance",
																content.cutAttributes
																	.outsideTolerance.display,
																defineCutParameters
																	?.outsideTolerance?.value,
																toleranceOptions,
																true
															)}
														{cutAttribute.name ===
															content.cutAttributes.insideDiameter
																.value &&
															renderCutAttributeInputField(
																"insideDiameter",
																`${content.cutAttributes.insideDiameter.display}, ${inch.display}`,
																defineCutParameters?.insideDiameter,
																"number",
																true,
																cutAttribute
															)}
														{cutAttribute.name ===
															content.cutAttributes.insideTolerance
																.value &&
															renderCutAttributeDropdown(
																"insideTolerance",
																content.cutAttributes
																	.insideTolerance.display,
																defineCutParameters?.insideTolerance
																	?.value,
																toleranceOptions,
																true
															)}
														{cutAttribute.name ===
															content.cutAttributes.longSide.value &&
															renderCutAttributeInputField(
																"longSide",
																`${content.cutAttributes.longSide.display}, ${inch.display}`,
																defineCutParameters?.longSide,
																"number",
																true,
																cutAttribute
															)}
														{cutAttribute.name ===
															content.cutAttributes.shortSide.value &&
															renderCutAttributeInputField(
																"shortSide",
																`${content.cutAttributes.shortSide.display}, ${inch.display}`,
																defineCutParameters?.shortSide,
																"number",
																true,
																cutAttribute
															)}
														{cutAttribute.name ===
															content.cutAttributes
																.widthLengthTolerance.value &&
															renderCutAttributeDropdown(
																"widthLengthTolerance",
																content.cutAttributes
																	.widthLengthTolerance.display,
																defineCutParameters
																	?.widthLengthTolerance?.value,
																toleranceOptions,
																true
															)}
													</FlexItem>
												);
											}
										)}
									</Flex>
								</>
							)}
					</ContentSection>
				</Flex>
			</ModalContainer>
		</ContentSection>
	);
};

export default DefineCutModalDesktop;
